/**
 * License Mode Sync Hook
 *
 * Syncs the current license mode to the main process for context file generation.
 * This ensures AI workspace context files have the correct license-aware content.
 */

import { useEffect, useRef } from 'react';
import { useLicenseStore } from '../store/licenseStore';
import { useHasProAccess } from './useHasProAccess';
import type { LicenseContextMode } from '../types';

/**
 * Compute the LicenseContextMode from license store state.
 */
function computeLicenseContextMode(
  hasProAccess: boolean,
  trialState: 'not-started' | 'active' | 'expired' | 'skipped',
  daysRemaining: number
): LicenseContextMode {
  // Active trial must be represented explicitly so the main process can
  // generate the correct "Trial Mode - X days remaining" context header.
  if (trialState === 'active') {
    return { type: 'trial', daysRemaining };
  }

  if (hasProAccess) {
    return { type: 'pro' };
  }

  return { type: 'personal' };
}

/**
 * Hook that syncs license mode to main process whenever it changes.
 * Should be used in a top-level component (e.g., App.tsx).
 */
export function useLicenseModeSync(): void {
  const trialState = useLicenseStore((s) => s.getTrialState());
  const daysRemaining = useLicenseStore((s) => s.getTrialDaysRemaining());
  const isInitialized = useLicenseStore((s) => s.isInitialized);

  // Reactive Pro access (correctly subscribes to all sources)
  const hasProAccess = useHasProAccess();

  const lastSyncedRef = useRef<string | null>(null);

  useEffect(() => {
    // Wait for store to be initialized
    if (!isInitialized) return;

    // Skip if API not available
    const api = window.electronAPI?.aiWorkspace;
    if (!api?.updateLicenseMode) return;

    // Compute the license context mode
    const mode = computeLicenseContextMode(hasProAccess, trialState, daysRemaining);

    // Create a serialized key for comparison
    const modeKey = JSON.stringify(mode);

    // Skip if already synced
    if (lastSyncedRef.current === modeKey) return;
    lastSyncedRef.current = modeKey;

    // Sync to main process
    api.updateLicenseMode(mode).catch((error) => {
      console.error('[LicenseModeSync] Failed to sync license mode:', error);
    });

    console.log('[LicenseModeSync] Synced license mode:', mode.type);
  }, [hasProAccess, trialState, daysRemaining, isInitialized]);
}
